import { useState, type FormEvent } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

type Props = {
  /** Free-form source label, e.g. `feature_x_launch`. Stored as-is. */
  source: string;
  /** Helper line shown beneath the form */
  helper: string;
};

/**
 * Single-purpose email-capture form that writes to `marketing_v2_notify`.
 *
 * - Anyone (signed-out included) can submit; the INSERT policy is open.
 * - Unique constraint `(email, source)` means a duplicate submission silently
 *   succeeds for the user (we treat it as success either way).
 */
export const NotifyMeForm = ({ source, helper }: Props) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@") || trimmed.length < 5) {
      setStatus("error");
      setErrorMsg("Enter a valid email address.");
      return;
    }
    setStatus("submitting");
    setErrorMsg(null);

    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
    const { error } = await supabase
      .from("marketing_v2_notify")
      .insert({ email: trimmed, source, user_agent: userAgent });

    // Unique violation = already on the list. Treat as success.
    if (error && error.code !== "23505") {
      setStatus("error");
      setErrorMsg("Couldn't save your email — try again in a moment.");
      return;
    }
    setStatus("success");
  };

  if (status === "success") {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-left"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="text-sm">
          <p className="font-medium text-foreground">You're on the list.</p>
          <p className="mt-1 text-muted-foreground">We'll email you once on launch day. Nothing else.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 text-left">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "submitting"}
          aria-label="Email address"
          className="sm:flex-1"
        />
        <Button type="submit" disabled={status === "submitting"} className="sm:min-w-32">
          {status === "submitting" ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            "Notify me"
          )}
        </Button>
      </div>
      {status === "error" && errorMsg && (
        <p className="text-sm text-destructive" role="alert">{errorMsg}</p>
      )}
      <p className="text-xs text-muted-foreground">{helper}</p>
    </form>
  );
};
